import { createContext, ReactNode, useContext, useMemo } from 'react';
import { IIdentityContext } from '@almamedia-legal-insights/types';

// ============================================================================
// Types
// ============================================================================
interface UserManagementContextValue {
  identity: IIdentityContext;
  userProducts: string[];
}

// ============================================================================
// Context Creation
// ============================================================================
const UserManagementContext = createContext<UserManagementContextValue | null>(
  null,
);

interface UserManagementProviderProps {
  identity: IIdentityContext;
  children: ReactNode;
}

export function UserManagementProvider({
  identity,
  children,
}: Readonly<UserManagementProviderProps>) {
  const contextValue = useMemo<UserManagementContextValue>(
    () => ({
      identity,
      userProducts:
        identity.authInfo?.productRights?.map((right) => right.productNumber) ||
        [],
    }),
    [identity],
  );

  return (
    <UserManagementContext.Provider value={contextValue}>
      {children}
    </UserManagementContext.Provider>
  );
}

// ============================================================================
// Custom Hook
// ============================================================================
// eslint-disable-next-line react-refresh/only-export-components
export function useUserManagement() {
  const context = useContext(UserManagementContext);

  if (!context) {
    throw new Error(
      'useUserManagement must be used within a UserManagementProvider',
    );
  }

  return context;
}
